import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Radio, ChevronRight, Volume2, VolumeX, SkipForward, Loader2, MessageCircle, Clock } from "lucide-react";
import api from "@/lib/api";
import LiveChat from "@/components/tv/LiveChat";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatHour = (h) => {
  const hh = ((h % 24) + 24) % 24;
  if (hh === 0) return "12 AM";
  if (hh === 12) return "12 PM";
  return hh < 12 ? `${hh} AM` : `${hh - 12} PM`;
};

const toYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

// Weekday: 0 = Mon..6 = Sun (matches our schedule seed)
const currentDayIndex = () => {
  const js = new Date().getDay(); // 0 = Sun .. 6 = Sat
  return js === 0 ? 6 : js - 1;
};

// ---------------------------------------------------------------------------
// Page — 24/7 continuous player
// ---------------------------------------------------------------------------
export default function TVLive() {
  const [settings, setSettings] = useState(null);
  const [shows, setShows] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [episodesByShow, setEpisodesByShow] = useState({});  // { slug: [episode,...] }
  const [loading, setLoading] = useState(true);

  const [currentShow, setCurrentShow] = useState(null);
  const [currentBlock, setCurrentBlock] = useState(null);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [muted, setMuted] = useState(true);   // autoplay policies require muted start
  const [tick, setTick] = useState(0);        // re-render every minute

  // Refs for player iframe + rotating playlist index
  const playlistIdx = useRef(0);

  // ------------------ initial data ------------------
  useEffect(() => {
    Promise.all([
      api.get("/tv/settings"),
      api.get("/tv/shows"),
      api.get("/tv/schedule"),
    ]).then(([s, sh, sc]) => {
      setSettings(s.data);
      setShows(sh.data || []);
      setSchedule(sc.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Lazy-fetch episodes for a show, cache them
  const ensureEpisodes = async (slug) => {
    if (episodesByShow[slug]?.length) return episodesByShow[slug];
    try {
      const { data } = await api.get(`/tv/episodes?show=${slug}&limit=50`);
      // Only include episodes that actually have a video URL (skip placeholders)
      const playable = (data || []).filter((e) => e.video_url);
      setEpisodesByShow((prev) => ({ ...prev, [slug]: playable }));
      return playable;
    } catch {
      return [];
    }
  };

  // Cross-show fallback: find *any* playable episode when the on-air show has none.
  // Guarantees viewers always see content instead of a colour-bars fallback screen.
  const fetchAnyPlayableEpisode = async () => {
    for (const s of shows) {
      const eps = await ensureEpisodes(s.slug);
      if (eps.length) return { show: s, episode: eps[Math.floor(Math.random() * eps.length)] };
    }
    return null;
  };

  // ------------------ core: figure out what should be on right now ------------------
  const computeNow = async () => {
    if (!schedule.length || !shows.length) return;
    const now = new Date();
    const day = currentDayIndex();
    const hour = now.getHours();
    const block = schedule.find((b) => b.day === day && b.start_hour <= hour && b.end_hour > hour);
    if (!block) {
      // Off-air fallback — pick a featured episode from any show
      const fb = await fetchAnyPlayableEpisode();
      setCurrentBlock(null);
      if (fb) {
        setCurrentShow(fb.show);
        setCurrentEpisode(fb.episode);
      }
      return;
    }
    const show = shows.find((s) => s.slug === block.show_slug);
    if (!show) return;
    const eps = await ensureEpisodes(show.slug);
    if (eps.length === 0) {
      // No playable episodes yet in this block — borrow from another show so the
      // channel stays live instead of showing the colour-bars fallback.
      const fb = await fetchAnyPlayableEpisode();
      setCurrentBlock(block);
      if (fb) {
        setCurrentShow(fb.show);
        setCurrentEpisode(fb.episode);
      } else {
        setCurrentShow(show);
        setCurrentEpisode(null);
      }
      return;
    }
    // Rotate by day-of-month + minute inside the block for freshness
    const rotationSeed = (now.getDate() * 60 + now.getMinutes()) % eps.length;
    playlistIdx.current = rotationSeed;
    const ep = eps[rotationSeed];
    setCurrentBlock(block);
    setCurrentShow(show);
    setCurrentEpisode(ep);
  };

  useEffect(() => { computeNow(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [schedule, shows]);

  // Re-check schedule every 60s (drives the ticker + top-of-hour swap)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    // On every tick, check whether the block has changed; if so, re-compute
    const now = new Date();
    const day = currentDayIndex();
    const hour = now.getHours();
    const shouldBlock = schedule.find((b) => b.day === day && b.start_hour <= hour && b.end_hour > hour);
    if (shouldBlock?.id !== currentBlock?.id) {
      computeNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  // ------------------ up next ------------------
  const upNext = useMemo(() => {
    if (!schedule.length || !shows.length) return [];
    const now = new Date();
    const day = currentDayIndex();
    const hour = now.getHours();
    const items = [];
    // Look 8 hours forward across day boundaries
    for (let offset = 1; offset <= 8; offset++) {
      const h = (hour + offset) % 24;
      const d = (day + Math.floor((hour + offset) / 24)) % 7;
      const block = schedule.find((b) => b.day === d && b.start_hour <= h && b.end_hour > h);
      if (!block) continue;
      if (items.length && items[items.length - 1].block.id === block.id) continue;
      const show = shows.find((s) => s.slug === block.show_slug);
      if (!show) continue;
      items.push({ block, show, hour: h, day: d });
      if (items.length >= 4) break;
    }
    return items;
  }, [schedule, shows, tick]);

  // ------------------ skip ------------------
  const skipNext = async () => {
    if (!currentShow) return;
    const eps = await ensureEpisodes(currentShow.slug);
    if (eps.length <= 1) return;
    playlistIdx.current = (playlistIdx.current + 1) % eps.length;
    setCurrentEpisode(eps[playlistIdx.current]);
  };

  // ------------------ derived ------------------
  const ytId = toYouTubeId(currentEpisode?.video_url);

  // ------------------ render ------------------
  if (loading) {
    return (
      <div className="min-h-[100vh] bg-ink flex items-center justify-center" data-testid="tv-live-loading">
        <Loader2 size={26} className="animate-spin text-gold-400 mr-3" />
        <span className="text-white/60 uppercase tracking-[0.22em] text-sm">Tuning HBTV…</span>
      </div>
    );
  }

  return (
    <div data-testid="tv-live-page" className="min-h-screen bg-ink text-white">
      {/* Compact top bar */}
      <div className="sticky top-0 z-30 border-b border-gold-500/25 bg-ink/90 backdrop-blur pt-24 md:pt-28">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/tv" data-testid="tv-live-back" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300 transition-colors shrink-0">
              <ArrowLeft size={13} /> HBTV
            </Link>
            <span className="hidden sm:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/50">
              <span className="relative inline-flex w-2 h-2"><span className="absolute inline-flex w-full h-full rounded-full bg-red-500 opacity-75 animate-ping" /><span className="relative inline-flex w-2 h-2 rounded-full bg-red-500" /></span>
              Broadcasting 24/7
            </span>
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.24em] text-white/40">
              · {settings?.timezone_label || "WAT · Cameroon"}
            </span>
          </div>
          {settings?.youtube_channel_url && (
            <a
              href={settings.youtube_channel_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tv-live-youtube"
              className="hidden sm:inline-flex items-center gap-2 border border-red-500/50 hover:border-red-400 text-red-300 hover:text-red-200 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] transition-colors"
            >
              Watch on YouTube
            </a>
          )}
        </div>
      </div>

      {/* Player + sidebar */}
      <div className="mx-auto max-w-[1500px] px-4 md:px-10 py-6 md:py-10">
        <div className="grid gap-6 lg:grid-cols-12">
          {/* Player */}
          <div className="lg:col-span-8">
            <div className="relative aspect-video overflow-hidden border border-gold-500/25 bg-black shadow-[0_60px_120px_-40px_rgba(11,32,83,0.9)]" data-testid="tv-live-player">
              {ytId ? (
                <>
                  <iframe
                    key={ytId}
                    src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&mute=${muted ? 1 : 0}&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${window.location.origin}`}
                    title={currentEpisode?.title}
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                    className="w-full h-full"
                    data-testid="tv-live-iframe"
                  />
                  {/* Fallback CTA: shows a small "Open on YouTube" chip so viewers can
                      escape to YouTube.com if the embed refuses (copyright-matched
                      audio, age-gate, region-block, or unsaved "Allow embedding" flag). */}
                  <a
                    href={`https://www.youtube.com/watch?v=${ytId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tv-live-open-in-yt"
                    className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-black/70 hover:bg-red-600 text-white text-[10px] uppercase tracking-[0.2em] px-2.5 py-1.5 backdrop-blur-sm transition-colors z-10"
                    title="If the embed shows 'Video unavailable', open this video on YouTube directly."
                  >
                    Open on YouTube ↗
                  </a>
                </>
              ) : currentEpisode?.video_url ? (
                <video
                  key={currentEpisode.id}
                  src={currentEpisode.video_url}
                  poster={currentEpisode.thumbnail}
                  autoPlay
                  muted={muted}
                  playsInline
                  controls
                  onEnded={skipNext}
                  className="w-full h-full object-cover"
                />
              ) : (
                // Off-air / no playable episodes yet
                <div className="absolute inset-0 flex items-center justify-center text-center px-8" data-testid="tv-live-placeholder">
                  {currentShow?.cover_image && <img src={currentShow.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/60" />
                  <div className="relative max-w-lg">
                    <motion.div animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2.4, repeat: Infinity }} className="mx-auto w-16 h-16 rounded-full border border-gold-500/40 bg-gold-500/10 flex items-center justify-center mb-5">
                      <Radio size={26} className="text-gold-400" strokeWidth={1.4} />
                    </motion.div>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-gold-400 mb-3">HBTV · Colour Bars</p>
                    <p className="font-serif text-white text-2xl md:text-3xl leading-tight">
                      {currentShow ? `${currentShow.title} — episodes coming soon` : "Broadcast starting soon"}
                    </p>
                    <p className="mt-3 text-white/60 text-sm leading-relaxed">
                      This time slot is scheduled but no playable episode is loaded yet. An admin can import from YouTube in seconds — head to the admin dashboard.
                    </p>
                  </div>
                </div>
              )}

              {/* Overlay HUD */}
              <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
                <span className="inline-flex items-center gap-2 bg-red-600/90 text-white px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-[0.24em] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> On Air
                </span>
                {currentShow && (
                  <span className="hidden sm:inline-flex items-center gap-1.5 bg-ink/70 backdrop-blur border border-gold-500/40 px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-[0.22em] text-gold-300" data-testid="tv-live-onshow">
                    {currentShow.title}
                  </span>
                )}
              </div>

              {/* Controls */}
              <div className="absolute bottom-3 right-3 flex items-center gap-2 z-10">
                <button
                  onClick={() => setMuted((m) => !m)}
                  data-testid="tv-live-mute"
                  aria-label={muted ? "Unmute" : "Mute"}
                  className="w-9 h-9 rounded-full bg-ink/80 backdrop-blur border border-gold-500/40 hover:border-gold-400 text-white/80 hover:text-gold-300 flex items-center justify-center transition-colors"
                >
                  {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
                </button>
                <button
                  onClick={skipNext}
                  disabled={!currentShow}
                  data-testid="tv-live-skip"
                  aria-label="Skip"
                  className="w-9 h-9 rounded-full bg-ink/80 backdrop-blur border border-gold-500/40 hover:border-gold-400 text-white/80 hover:text-gold-300 flex items-center justify-center transition-colors disabled:opacity-50"
                >
                  <SkipForward size={15} />
                </button>
              </div>
            </div>

            {/* Metadata */}
            <div className="mt-6">
              {currentBlock && (
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold-500">
                  {DAYS_SHORT[currentBlock.day]} · {formatHour(currentBlock.start_hour)} → {formatHour(currentBlock.end_hour)}
                  {currentShow && <> · <span className="text-white/70">{currentShow.category}</span></>}
                </p>
              )}
              {currentShow && (
                <h1 className="mt-3 font-serif text-3xl md:text-4xl leading-tight text-white" data-testid="tv-live-title">
                  {currentEpisode?.title || currentShow.title}
                </h1>
              )}
              {currentEpisode?.description && (
                <p className="mt-4 text-white/70 leading-relaxed max-w-3xl">{currentEpisode.description}</p>
              )}
            </div>
          </div>

          {/* Sidebar — Coming Up + Live Chat tabs */}
          <aside className="lg:col-span-4">
            <SidebarTabs upNext={upNext} />
          </aside>
        </div>
      </div>
    </div>
  );
}

// Local sub-component keeps the tab state inside the sidebar without touching player state
function SidebarTabs({ upNext }) {
  const [tab, setTab] = useState("upnext");

  return (
    <div>
      <div className="flex gap-2 mb-4" role="tablist" data-testid="tv-live-sidebar-tabs">
        <button
          role="tab"
          aria-selected={tab === "upnext"}
          onClick={() => setTab("upnext")}
          data-testid="tv-live-tab-upnext"
          className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-[10px] uppercase tracking-[0.24em] border transition-colors ${
            tab === "upnext"
              ? "border-gold-500/70 bg-gold-500/10 text-gold-300"
              : "border-white/10 text-white/50 hover:text-white hover:border-white/20"
          }`}
        >
          <Clock size={12} /> Coming Up
        </button>
        <button
          role="tab"
          aria-selected={tab === "chat"}
          onClick={() => setTab("chat")}
          data-testid="tv-live-tab-chat"
          className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-[10px] uppercase tracking-[0.24em] border transition-colors ${
            tab === "chat"
              ? "border-gold-500/70 bg-gold-500/10 text-gold-300"
              : "border-white/10 text-white/50 hover:text-white hover:border-white/20"
          }`}
        >
          <MessageCircle size={12} /> Live Chat
        </button>
      </div>

      {tab === "upnext" ? (
        <div data-testid="tv-live-tab-panel-upnext">
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-500 mb-4">Coming up on HBTV</p>
          <ul className="space-y-3" data-testid="tv-live-upnext">
            <AnimatePresence mode="popLayout">
              {upNext.length === 0 && (
                <li className="text-white/50 text-sm">Schedule updating…</li>
              )}
              {upNext.map((u, i) => (
                <motion.li
                  key={`${u.block.id}-${i}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                >
                  <Link
                    to={`/tv/show/${u.show.slug}`}
                    className="group flex gap-3 border border-gold-500/15 hover:border-gold-400 bg-ink-paper/40 hover:bg-ink-paper transition-colors duration-200 p-3"
                  >
                    <div className="w-28 aspect-video shrink-0 overflow-hidden border border-gold-500/20 relative">
                      <img src={u.show.cover_image} alt={u.show.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
                      <span className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: u.show.accent_color || "#D4AF37" }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-gold-400">
                        {formatHour(u.hour)} · {DAYS_SHORT[u.day]}
                      </p>
                      <p className="mt-1 text-white text-sm leading-snug line-clamp-2 group-hover:text-gold-200 transition-colors">{u.block.label || u.show.title}</p>
                      <p className="text-white/50 text-xs leading-snug line-clamp-1">{u.show.tagline}</p>
                    </div>
                    <ChevronRight size={16} className="text-gold-500/70 self-center shrink-0" />
                  </Link>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
          <Link to="/tv" data-testid="tv-live-fullsched" className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300 transition-colors">
            See full weekly schedule →
          </Link>
        </div>
      ) : (
        <div data-testid="tv-live-tab-panel-chat">
          <LiveChat />
        </div>
      )}
    </div>
  );
}
